import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, extractError } from '@/lib/api.js';
import { Dica } from '@/components/Dica.js';
import { TEMAS } from '@shared/conselheiro.js';
import { Anexos, type Anexo } from './Anexos.js';
import { SalaDoMeet } from './SalaDoMeet.js';

/**
 * O mês do Conselheiro: a próxima manhã, o preenchimento da empresa antes
 * dela, a pauta e as decisões. O Principal agenda, monta a pauta e registra
 * decisões; sócios e responsáveis preenchem, propõem temas e dão andamento.
 */

type Manha = {
  id: string;
  tipo: 'S0' | 'MENSAL' | 'MARCO' | 'FECHAMENTO';
  formato: 'PRESENCIAL' | 'ONLINE';
  local: string | null;
  meet_link: string | null;
  inicio_previsto: string;
  status: string;
  preenchimento_ate: string | null;
  preenchido_em: string | null;
  preenchimento: 'FEITO' | 'NO_PRAZO' | 'ATRASADO';
  relato_empresa: string | null;
  pauta_confirmada_em: string | null;
  temas: {
    id: string;
    titulo: string;
    contexto: string | null;
    origem: string;
    tema: number | null;
    status: string;
    ordem: number;
    meu: boolean;
  }[];
  anexos: Anexo[];
  inicio_real: string | null;
  fim_real: string | null;
  avaliacao: { minha: number | null; media: number | null; total: number };
};
type Decisao = {
  id: string;
  descricao: string;
  motivo: string | null;
  tema: number | null;
  prazo: string | null;
  indicador_sucesso: string | null;
  status: string;
  atrasada: boolean;
  responsavel: { membro_id: string; nome: string; user_id: string } | null;
  atualizacoes: { status: string; comentario: string | null; autor: string; em: string }[];
};
type Mes = {
  quem: 'CONSELHEIRO' | 'SOCIO' | 'RESPONSAVEL';
  ciclo: { id: string } | null;
  max_temas_da_empresa: number;
  anexos_disponiveis: boolean;
  proxima_manha_id: string | null;
  manha_em_andamento_id: string | null;
  manhas: Manha[];
  decisoes: Decisao[];
  responsaveis_possiveis: { membro_id: string; nome: string }[];
};

const TIPO: Record<string, string> = {
  S0: 'S0 (primeira manhã)',
  MENSAL: 'Mensal',
  MARCO: 'Marco',
  FECHAMENTO: 'Fechamento',
};
const STATUS_MANHA: Record<string, string> = {
  AGENDADA: 'Agendada',
  PREPARACAO: 'Em preparação',
  PAUTA_CONFIRMADA: 'Pauta confirmada',
  EM_ANDAMENTO: 'Em andamento',
  REALIZADA: 'Realizada',
  CANCELADA: 'Cancelada',
};
const STATUS_DECISAO: Record<string, string> = {
  ABERTA: 'Aberta',
  EM_DIA: 'Em dia',
  EM_RISCO: 'Em risco',
  BLOQUEADA: 'Bloqueada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};
const COR_DECISAO: Record<string, string> = {
  ABERTA: 'bg-slate-500/15 text-ha-text',
  EM_DIA: 'bg-emerald-500/15 text-emerald-600',
  EM_RISCO: 'bg-amber-500/15 text-amber-600',
  BLOQUEADA: 'bg-red-500/15 text-red-600',
  CONCLUIDA: 'bg-emerald-500/25 text-emerald-700',
  CANCELADA: 'bg-slate-500/10 text-ha-muted',
};
const ORIGEM: Record<string, string> = { EMPRESA: 'empresa', CONSELHEIRO: 'conselheiro', DESVIO: 'desvio' };

const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
const dia = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

const campo = 'rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm';
const botao = 'rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50';

function Bloco({
  titulo,
  dica,
  acao,
  children,
}: {
  titulo: string;
  dica?: string;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">
          {titulo}
          {dica ? <Dica texto={dica} /> : null}
        </h3>
        {acao}
      </div>
      {children}
    </div>
  );
}

function Rotulado({
  rotulo,
  dica,
  children,
  className,
}: {
  rotulo: string;
  dica?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="flex items-center gap-1 text-[11px] font-medium text-ha-muted">
        {rotulo}
        {dica ? <Dica texto={dica} rotulo={`Ajuda: ${rotulo}`} /> : null}
      </span>
      {children}
    </label>
  );
}

const msg = (e: unknown) => (e instanceof Error && !('response' in e) ? e.message : extractError(e).message);

export function CicloMensal({ empresaId, principal }: { empresaId: string; principal: boolean }) {
  const qc = useQueryClient();
  const chave = ['conselheiro-mes', empresaId];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () => (await api.get<Mes>(`/conselheiro/empresas/${empresaId}/mes`)).data,
  });
  const [erro, setErro] = useState<string | null>(null);
  const escrever = useMutation({
    mutationFn: async (f: () => Promise<{ data: Mes }>) => (await f()).data,
    onSuccess: (dados) => {
      setErro(null);
      qc.setQueryData(chave, dados);
    },
    onError: (e) => setErro(msg(e)),
  });
  const enviar = (f: () => Promise<{ data: Mes }>) =>
    escrever.mutateAsync(f).then(
      () => true,
      () => false,
    );

  if (q.isLoading) return <p className="text-sm text-ha-subtle">Carregando o mês…</p>;
  if (q.isError || !q.data) return <p className="text-sm text-red-600">{extractError(q.error).message}</p>;
  const m = q.data;
  if (!m.ciclo) return <p className="text-sm text-ha-subtle">Sem ciclo ativo.</p>;
  const empresa = m.quem !== 'CONSELHEIRO';
  const proxima = m.manhas.find((x) => x.id === m.proxima_manha_id) ?? null;
  const passadas = m.manhas.filter(
    (x) => x.id !== m.proxima_manha_id && (x.status === 'REALIZADA' || new Date(x.inicio_previsto) < new Date()),
  );

  return (
    <div className="flex flex-col gap-6">
      {erro ? <p className="text-sm text-red-600">{erro}</p> : null}
      {m.manha_em_andamento_id ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-ha-border2 p-3 text-sm">
          <span className="font-semibold text-ha-text">A manhã do conselho está em andamento.</span>
          <span className="flex flex-wrap items-center gap-3">
            {m.manhas.find((x) => x.id === m.manha_em_andamento_id)?.meet_link ? (
              <SalaDoMeet
                manhaId={m.manha_em_andamento_id}
                link={m.manhas.find((x) => x.id === m.manha_em_andamento_id)!.meet_link}
                principal={principal}
                aoCriar={() => undefined}
                compacto
              />
            ) : null}
            <Link to={`/direcao/conselheiro/manha/${m.manha_em_andamento_id}`} className="text-accent underline">
              {principal ? 'Voltar à condução' : 'Acompanhar'}
            </Link>
          </span>
        </div>
      ) : null}

      <Bloco
        titulo="Próxima manhã"
        dica="A manhã do conselho: uma por mês, presencial de preferência. A empresa preenche até 5 dias antes."
      >
        {proxima ? (
          <ProximaManha manha={proxima} principal={principal} enviar={enviar} />
        ) : (
          <p className="text-sm text-ha-subtle">
            {principal ? 'Nenhuma manhã agendada.' : 'O conselheiro ainda não agendou a próxima manhã.'}
          </p>
        )}
        {principal && !proxima ? <AgendarManha empresaId={empresaId} enviar={enviar} /> : null}
      </Bloco>

      {proxima ? (
        <>
          <Preenchimento manha={proxima} empresa={empresa} enviar={enviar} />
          <Bloco
            titulo="Anexos da manhã"
            dica="Relatórios, planilhas e apresentações para o conselheiro preparar a manhã. Ficam guardados com acesso restrito à empresa e ao conselheiro."
          >
            <Anexos
              empresaId={empresaId}
              manhaId={proxima.id}
              anexos={proxima.anexos}
              disponivel={m.anexos_disponiveis}
              conselheiro={m.quem === 'CONSELHEIRO'}
            />
          </Bloco>
          <Pauta manha={proxima} quem={m.quem} principal={principal} max={m.max_temas_da_empresa} enviar={enviar} />
        </>
      ) : null}

      <Decisoes
        empresaId={empresaId}
        decisoes={m.decisoes}
        podeRegistrar={principal || m.quem === 'SOCIO'}
        responsaveis={m.responsaveis_possiveis}
        manhaId={proxima?.id ?? null}
        enviar={enviar}
      />

      {passadas.length > 0 ? (
        <Bloco titulo="Manhãs anteriores">
          <ul className="flex flex-col gap-1 text-sm">
            {passadas.map((x) => (
              <li key={x.id} className="text-ha-subtle">
                {quando(x.inicio_previsto)} · {TIPO[x.tipo]} · {STATUS_MANHA[x.status] ?? x.status}
                {x.inicio_real ? (
                  <>
                    {' · '}
                    <Link to={`/direcao/conselheiro/manha/${x.id}`} className="text-accent underline">
                      ver a manhã
                    </Link>
                  </>
                ) : null}
                {x.status === 'REALIZADA' ? <Avaliacao manha={x} socio={m.quem === 'SOCIO'} enviar={enviar} /> : null}
                {x.anexos.length > 0 ? (
                  <div className="mt-1 pl-3">
                    <Anexos
                      empresaId={empresaId}
                      manhaId={x.id}
                      anexos={x.anexos}
                      disponivel={m.anexos_disponiveis}
                      conselheiro={m.quem === 'CONSELHEIRO'}
                      podeAnexar={false}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </Bloco>
      ) : null}
    </div>
  );
}

type Enviar = (f: () => Promise<{ data: Mes }>) => Promise<boolean>;

function ProximaManha({ manha, principal, enviar }: { manha: Manha; principal: boolean; enviar: Enviar }) {
  const qc = useQueryClient();
  return (
    <div className="rounded-card border border-ha-border2 p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold text-ha-text">
          {quando(manha.inicio_previsto)} · {TIPO[manha.tipo]}
        </span>
        <span className="text-xs text-ha-muted">{STATUS_MANHA[manha.status] ?? manha.status}</span>
      </div>
      <p className="mt-1 text-xs text-ha-muted">
        {manha.formato === 'ONLINE' ? 'Online' : 'Presencial'}
        {manha.local ? ` · ${manha.local}` : ''} · preenchimento da empresa até {dia(manha.preenchimento_ate)}
      </p>
      {manha.formato === 'ONLINE' ? (
        <div className="mt-2">
          <SalaDoMeet
            manhaId={manha.id}
            link={manha.meet_link}
            principal={principal}
            aoCriar={() => void qc.invalidateQueries({ queryKey: ['conselheiro-mes'] })}
          />
        </div>
      ) : null}
      {principal ? (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <IniciarManha manhaId={manha.id} />
          <Link to={`/direcao/conselheiro/manha/${manha.id}`} className="text-accent underline">
            Dossiê de preparação
          </Link>
          <button
            type="button"
            className="text-accent underline"
            onClick={() =>
              window.confirm('Marcar esta manhã como realizada? Os temas da pauta passam a discutidos.') &&
              enviar(() => api.patch(`/conselheiro/manhas/${manha.id}`, { status: 'REALIZADA' }))
            }
          >
            Marcar realizada
          </button>
          <button
            type="button"
            className="text-ha-muted underline"
            onClick={() =>
              window.confirm('Cancelar esta manhã? Os temas propostos voltam a esperar a próxima.') &&
              enviar(() => api.patch(`/conselheiro/manhas/${manha.id}`, { status: 'CANCELADA' }))
            }
          >
            Cancelar
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AgendarManha({ empresaId, enviar }: { empresaId: string; enviar: Enviar }) {
  const [f, setF] = useState({ tipo: 'MENSAL', inicio: '', formato: 'PRESENCIAL', local: '' });
  return (
    <div className="mt-3 grid gap-2 rounded-card border border-ha-border2 p-3 sm:grid-cols-[1fr_1.4fr_1fr_1.6fr_auto] sm:items-end">
      <Rotulado rotulo="Tipo" dica="S0 é a primeira manhã do ciclo, onde se definem metas e indicadores.">
        <select className={campo} value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>
          {Object.entries(TIPO).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </Rotulado>
      <Rotulado rotulo="Quando">
        <input
          type="datetime-local"
          className={campo}
          value={f.inicio}
          onChange={(e) => setF({ ...f, inicio: e.target.value })}
        />
      </Rotulado>
      <Rotulado rotulo="Formato" dica="Presencial é o padrão do método; online quando não houver como.">
        <select className={campo} value={f.formato} onChange={(e) => setF({ ...f, formato: e.target.value })}>
          <option value="PRESENCIAL">Presencial</option>
          <option value="ONLINE">Online</option>
        </select>
      </Rotulado>
      <Rotulado rotulo="Local ou link">
        <input className={campo} value={f.local} onChange={(e) => setF({ ...f, local: e.target.value })} />
      </Rotulado>
      <button
        type="button"
        className={botao}
        style={{ background: 'var(--primary)' }}
        disabled={!f.inicio}
        onClick={() =>
          enviar(() =>
            api.post(`/conselheiro/empresas/${empresaId}/manhas`, {
              tipo: f.tipo,
              inicio_previsto: new Date(f.inicio).toISOString(),
              formato: f.formato,
              local: f.local.trim() || null,
            }),
          )
        }
      >
        Agendar
      </button>
    </div>
  );
}

function Preenchimento({ manha, empresa, enviar }: { manha: Manha; empresa: boolean; enviar: Enviar }) {
  const [relato, setRelato] = useState(manha.relato_empresa ?? '');
  const estado =
    manha.preenchimento === 'FEITO'
      ? `Feito em ${dia(manha.preenchido_em)}.`
      : manha.preenchimento === 'ATRASADO'
        ? `Atrasado: o prazo era ${dia(manha.preenchimento_ate)}.`
        : `Prazo: ${dia(manha.preenchimento_ate)}.`;
  return (
    <Bloco
      titulo="Preenchimento da empresa"
      dica="Uns 15 minutos antes de cada manhã: o relato do mês, os indicadores do mês, o andamento das decisões e até 3 temas para a pauta."
    >
      <p className={`mb-2 text-xs ${manha.preenchimento === 'ATRASADO' ? 'text-red-600' : 'text-ha-muted'}`}>
        {estado}
      </p>
      {empresa ? (
        <div className="flex flex-col gap-2">
          <ol className="list-decimal pl-5 text-xs text-ha-subtle">
            <li>Informe os indicadores do mês, no plano do ciclo.</li>
            <li>Dê andamento às decisões abaixo.</li>
            <li>Proponha até 3 temas para a pauta.</li>
            <li>Conte como foi o mês e conclua.</li>
          </ol>
          <textarea
            className={campo}
            rows={4}
            placeholder="Como foi o mês: o que avançou, o que travou, o que mudou."
            value={relato}
            onChange={(e) => setRelato(e.target.value)}
          />
          <div>
            <button
              type="button"
              className={botao}
              style={{ background: 'var(--primary)' }}
              disabled={relato.trim().length < 10}
              onClick={() => enviar(() => api.post(`/conselheiro/manhas/${manha.id}/preenchimento`, { relato }))}
            >
              {manha.preenchido_em ? 'Atualizar relato' : 'Concluir preenchimento'}
            </button>
          </div>
        </div>
      ) : manha.relato_empresa ? (
        <p className="whitespace-pre-wrap rounded-card border border-ha-border2 p-3 text-sm text-ha-text">
          {manha.relato_empresa}
        </p>
      ) : (
        <p className="text-sm text-ha-subtle">A empresa ainda não preencheu.</p>
      )}
    </Bloco>
  );
}

function Pauta({
  manha,
  quem,
  principal,
  max,
  enviar,
}: {
  manha: Manha;
  quem: Mes['quem'];
  principal: boolean;
  max: number;
  enviar: Enviar;
}) {
  const [novo, setNovo] = useState({ titulo: '', contexto: '', tema: '' });
  const [escolha, setEscolha] = useState<string[] | null>(null);
  const daEmpresa = manha.temas.filter((t) => t.origem === 'EMPRESA').length;
  const podePropor = quem === 'CONSELHEIRO' || daEmpresa < max;
  const selecionados = useMemo(
    () =>
      escolha ??
      manha.temas
        .filter((t) => t.status === 'NA_PAUTA')
        .sort((a, b) => a.ordem - b.ordem)
        .map((t) => t.id),
    [escolha, manha.temas],
  );
  const mover = (id: string, d: number) => {
    const l = [...selecionados];
    const i = l.indexOf(id);
    const j = i + d;
    if (i < 0 || j < 0 || j >= l.length) return;
    [l[i], l[j]] = [l[j]!, l[i]!];
    setEscolha(l);
  };

  return (
    <Bloco
      titulo="Pauta"
      dica="A empresa propõe até 3 temas por manhã; o conselheiro também propõe e confirma a pauta, na ordem em que será discutida."
    >
      {manha.pauta_confirmada_em ? (
        <p className="mb-2 text-xs text-emerald-600">Pauta confirmada em {dia(manha.pauta_confirmada_em)}.</p>
      ) : null}
      {manha.temas.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nenhum tema proposto ainda.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {[...manha.temas]
            .sort((a, b) => {
              const ia = selecionados.indexOf(a.id);
              const ib = selecionados.indexOf(b.id);
              return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
            })
            .map((t) => {
              const na = selecionados.includes(t.id);
              return (
                <li key={t.id} className="flex items-start gap-2 rounded-card border border-ha-border2 p-2 text-sm">
                  {principal ? (
                    <input
                      type="checkbox"
                      className="mt-1"
                      aria-label={`Incluir ${t.titulo} na pauta`}
                      checked={na}
                      onChange={(e) =>
                        setEscolha(e.target.checked ? [...selecionados, t.id] : selecionados.filter((x) => x !== t.id))
                      }
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-ha-text">{t.titulo}</span>{' '}
                    <span className="text-xs text-ha-muted">
                      {ORIGEM[t.origem] ?? t.origem}
                      {t.tema != null ? ` · ${TEMAS[t.tema]}` : ''}
                      {t.status === 'ADIADO' ? ' · adiado' : ''}
                    </span>
                    {t.contexto ? <p className="text-xs text-ha-subtle">{t.contexto}</p> : null}
                  </div>
                  {principal && na ? (
                    <span className="flex gap-1 text-xs">
                      <button
                        type="button"
                        aria-label="Subir"
                        className="text-ha-muted"
                        onClick={() => mover(t.id, -1)}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label="Descer"
                        className="text-ha-muted"
                        onClick={() => mover(t.id, 1)}
                      >
                        ▼
                      </button>
                    </span>
                  ) : null}
                  {t.meu && t.status === 'PROPOSTO' ? (
                    <button
                      type="button"
                      className="text-xs text-ha-muted underline"
                      onClick={() => enviar(() => api.delete(`/conselheiro/temas/${t.id}`))}
                    >
                      Retirar
                    </button>
                  ) : null}
                </li>
              );
            })}
        </ul>
      )}
      {principal && manha.temas.length > 0 ? (
        <button
          type="button"
          className={`${botao} mt-2`}
          style={{ background: 'var(--primary)' }}
          onClick={() =>
            enviar(() => api.post(`/conselheiro/manhas/${manha.id}/pauta`, { temas: selecionados })).then(() =>
              setEscolha(null),
            )
          }
        >
          Confirmar pauta ({selecionados.length})
        </button>
      ) : null}

      {podePropor ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
          <Rotulado
            rotulo="Novo tema"
            dica="Um assunto que precisa do conselho: uma decisão difícil, um problema que voltou, uma oportunidade."
          >
            <input
              className={campo}
              value={novo.titulo}
              onChange={(e) => setNovo({ ...novo, titulo: e.target.value })}
            />
          </Rotulado>
          <Rotulado rotulo="Tema do método">
            <select className={campo} value={novo.tema} onChange={(e) => setNovo({ ...novo, tema: e.target.value })}>
              <option value="">—</option>
              {TEMAS.map((t, i) => (
                <option key={t} value={i}>
                  {t}
                </option>
              ))}
            </select>
          </Rotulado>
          <button
            type="button"
            className={botao}
            style={{ background: 'var(--primary)' }}
            disabled={novo.titulo.trim().length < 3}
            onClick={() =>
              enviar(() =>
                api.post(`/conselheiro/manhas/${manha.id}/temas`, {
                  titulo: novo.titulo,
                  contexto: novo.contexto.trim() || null,
                  tema: novo.tema === '' ? null : Number(novo.tema),
                }),
              ).then((ok) => ok && setNovo({ titulo: '', contexto: '', tema: '' }))
            }
          >
            Propor
          </button>
          <textarea
            className={`${campo} sm:col-span-3`}
            rows={2}
            placeholder="Contexto (opcional): o que o conselho precisa saber"
            value={novo.contexto}
            onChange={(e) => setNovo({ ...novo, contexto: e.target.value })}
          />
          {quem !== 'CONSELHEIRO' ? (
            <p className="text-xs text-ha-muted sm:col-span-3">
              {daEmpresa} de {max} temas da empresa nesta manhã.
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-2 text-xs text-ha-muted">A empresa já propôs os {max} temas desta manhã.</p>
      )}
    </Bloco>
  );
}

function Decisoes({
  empresaId,
  decisoes,
  podeRegistrar,
  responsaveis,
  manhaId,
  enviar,
}: {
  empresaId: string;
  decisoes: Decisao[];
  podeRegistrar: boolean;
  responsaveis: Mes['responsaveis_possiveis'];
  manhaId: string | null;
  enviar: Enviar;
}) {
  const [ver, setVer] = useState<'abertas' | 'todas'>('abertas');
  const [nova, setNova] = useState({ descricao: '', responsavel: '', prazo: '', indicador: '', tema: '' });
  const [abrindo, setAbrindo] = useState(false);
  const lista =
    ver === 'abertas' ? decisoes.filter((d) => d.status !== 'CONCLUIDA' && d.status !== 'CANCELADA') : decisoes;

  return (
    <Bloco
      titulo="Decisões"
      dica="A decisão é sempre dos sócios; um responsável executa, com prazo e um sinal de que deu certo. Quem executa diz o andamento."
      acao={
        <div className="flex gap-3 text-xs">
          <button
            type="button"
            className={ver === 'abertas' ? 'font-semibold text-ha-text' : 'text-ha-muted'}
            onClick={() => setVer('abertas')}
          >
            Em aberto
          </button>
          <button
            type="button"
            className={ver === 'todas' ? 'font-semibold text-ha-text' : 'text-ha-muted'}
            onClick={() => setVer('todas')}
          >
            Todas
          </button>
          {podeRegistrar && !abrindo ? (
            <button type="button" className="text-accent underline" onClick={() => setAbrindo(true)}>
              Registrar decisão
            </button>
          ) : null}
        </div>
      }
    >
      {abrindo ? (
        <div className="mb-3 grid gap-2 rounded-card border border-ha-border2 p-3 sm:grid-cols-2">
          <Rotulado rotulo="Decisão" className="sm:col-span-2">
            <textarea
              className={campo}
              rows={2}
              value={nova.descricao}
              onChange={(e) => setNova({ ...nova, descricao: e.target.value })}
            />
          </Rotulado>
          <Rotulado rotulo="Responsável" dica="Quem executa: um sócio ou um responsável da empresa.">
            <select
              className={campo}
              value={nova.responsavel}
              onChange={(e) => setNova({ ...nova, responsavel: e.target.value })}
            >
              <option value="">—</option>
              {responsaveis.map((r) => (
                <option key={r.membro_id} value={r.membro_id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </Rotulado>
          <Rotulado rotulo="Prazo">
            <input
              type="date"
              className={campo}
              value={nova.prazo}
              onChange={(e) => setNova({ ...nova, prazo: e.target.value })}
            />
          </Rotulado>
          <Rotulado
            rotulo="Como saberemos que deu certo"
            dica="Um sinal concreto: um número, uma entrega, um fato observável."
          >
            <input
              className={campo}
              value={nova.indicador}
              onChange={(e) => setNova({ ...nova, indicador: e.target.value })}
            />
          </Rotulado>
          <Rotulado rotulo="Tema do método">
            <select className={campo} value={nova.tema} onChange={(e) => setNova({ ...nova, tema: e.target.value })}>
              <option value="">—</option>
              {TEMAS.map((t, i) => (
                <option key={t} value={i}>
                  {t}
                </option>
              ))}
            </select>
          </Rotulado>
          <div className="flex gap-2 sm:col-span-2">
            <button
              type="button"
              className={botao}
              style={{ background: 'var(--primary)' }}
              disabled={nova.descricao.trim().length < 5}
              onClick={() =>
                enviar(() =>
                  api.post(`/conselheiro/empresas/${empresaId}/decisoes`, {
                    descricao: nova.descricao,
                    responsavel_membro_id: nova.responsavel || null,
                    prazo: nova.prazo ? `${nova.prazo}T23:59:59` : null,
                    indicador_sucesso: nova.indicador.trim() || null,
                    tema: nova.tema === '' ? null : Number(nova.tema),
                    manha_id: manhaId,
                  }),
                ).then((ok) => {
                  if (ok) {
                    setNova({ descricao: '', responsavel: '', prazo: '', indicador: '', tema: '' });
                    setAbrindo(false);
                  }
                })
              }
            >
              Registrar
            </button>
            <button type="button" className="text-xs text-ha-muted underline" onClick={() => setAbrindo(false)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
      {lista.length === 0 ? (
        <p className="text-sm text-ha-subtle">
          {ver === 'abertas' ? 'Nenhuma decisão em aberto.' : 'Nenhuma decisão ainda.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {lista.map((d) => (
            <LinhaDaDecisao key={d.id} decisao={d} enviar={enviar} />
          ))}
        </ul>
      )}
    </Bloco>
  );
}

function LinhaDaDecisao({ decisao: d, enviar }: { decisao: Decisao; enviar: Enviar }) {
  const [aberta, setAberta] = useState(false);
  const [status, setStatus] = useState(d.status);
  const [comentario, setComentario] = useState('');
  const encerrada = d.status === 'CONCLUIDA' || d.status === 'CANCELADA';
  return (
    <li className="rounded-card border border-ha-border2 p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="text-ha-text">{d.descricao}</span>
        <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${COR_DECISAO[d.status] ?? ''}`}>
          {STATUS_DECISAO[d.status] ?? d.status}
          {d.atrasada ? ' · atrasada' : ''}
        </span>
      </div>
      <p className="mt-1 text-xs text-ha-muted">
        {d.responsavel ? d.responsavel.nome : 'sem responsável'} · prazo {dia(d.prazo)}
        {d.tema != null ? ` · ${TEMAS[d.tema]}` : ''}
        {d.indicador_sucesso ? ` · sucesso: ${d.indicador_sucesso}` : ''}
      </p>
      {d.atualizacoes[0]?.comentario ? (
        <p className="mt-1 text-xs text-ha-subtle">
          Último andamento ({d.atualizacoes[0].autor}): {d.atualizacoes[0].comentario}
        </p>
      ) : null}
      {!encerrada && !aberta ? (
        <button type="button" className="mt-1 text-xs text-accent underline" onClick={() => setAberta(true)}>
          Dar andamento
        </button>
      ) : null}
      {aberta ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select className={campo} value={status} onChange={(e) => setStatus(e.target.value)}>
            {Object.entries(STATUS_DECISAO).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <input
            className={`${campo} min-w-[200px] flex-1`}
            placeholder="O que aconteceu"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
          <button
            type="button"
            className={botao}
            style={{ background: 'var(--primary)' }}
            onClick={() =>
              enviar(() =>
                api.post(`/conselheiro/decisoes/${d.id}/atualizacoes`, {
                  status,
                  comentario: comentario.trim() || null,
                }),
              ).then((ok) => ok && setAberta(false))
            }
          >
            Salvar
          </button>
          <button type="button" className="text-xs text-ha-muted underline" onClick={() => setAberta(false)}>
            Cancelar
          </button>
        </div>
      ) : null}
    </li>
  );
}

/** O Principal começa a manhã: a pauta vira o roteiro em blocos, e a tela vai para a condução. */
function IniciarManha({ manhaId }: { manhaId: string }) {
  const navigate = useNavigate();
  const [erro, setErro] = useState<string | null>(null);
  const iniciar = useMutation({
    mutationFn: () => api.post(`/conselheiro/manhas/${manhaId}/iniciar`),
    onSuccess: () => navigate(`/direcao/conselheiro/manha/${manhaId}`),
    onError: (e) => setErro(extractError(e).message),
  });
  return (
    <>
      <button
        type="button"
        className={botao}
        style={{ background: 'var(--primary)' }}
        disabled={iniciar.isPending}
        onClick={() =>
          window.confirm('Iniciar a manhã agora? A pauta vira o roteiro e o cronômetro começa.') && iniciar.mutate()
        }
      >
        Iniciar a manhã
      </button>
      {erro ? <span className="text-red-600">{erro}</span> : null}
    </>
  );
}

/** Depois da manhã, cada sócio dá uma nota de 1 a 5; o conselheiro vê a média. */
function Avaliacao({ manha, socio, enviar }: { manha: Manha; socio: boolean; enviar: Enviar }) {
  const a = manha.avaliacao;
  if (!socio) {
    return a.total ? (
      <span className="text-xs">
        {' · '}avaliação {a.media!.toFixed(1).replace('.', ',')} ({a.total} {a.total === 1 ? 'sócio' : 'sócios'})
      </span>
    ) : null;
  }
  return (
    <span className="ml-2 inline-flex items-center gap-1 text-xs">
      {a.minha ? 'Sua nota:' : 'Como foi esta manhã?'}
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`Nota ${n}`}
          className={`h-6 w-6 rounded-element border border-ha-border2 ${a.minha && n <= a.minha ? 'font-bold text-black' : ''}`}
          style={a.minha && n <= a.minha ? { background: 'var(--primary)' } : undefined}
          onClick={() => enviar(() => api.post(`/conselheiro/manhas/${manha.id}/avaliacao`, { nota: n }))}
        >
          {n}
        </button>
      ))}
    </span>
  );
}
